import { createTheme, ThemeProvider } from '@mui/material/styles';
import { GlobalStyles } from '@mui/system';
import { useState, useEffect, createContext, useContext } from 'react';

// Dark mode context
const DarkModeContext = createContext();

export const useDarkMode = () => useContext(DarkModeContext);

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1565C0',
      light: '#42a5f5',
      dark: '#0d47a1',
      contrastText: '#fff',
    },
    secondary: {
      main: '#00BCD4',
      light: '#4DD0E1',
      dark: '#0097A7',
      contrastText: '#fff',
    },
    background: {
      default: '#F5F9FF',
      paper: '#ffffff',
    },
    text: {
      primary: '#1A237E',
      secondary: '#212121',
    },
  },
  typography: {
    fontSize: 14,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.2,
      marginBottom: '1rem',
      '@media (min-width:768px)': {
        fontSize: '2.5rem',
      },
    },
    h2: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.3,
      marginBottom: '0.875rem',
      '@media (min-width:768px)': {
        fontSize: '2rem',
      },
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.4,
      marginBottom: '0.75rem',
      '@media (min-width:768px)': {
        fontSize: '1.75rem',
      },
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.4,
      marginBottom: '0.5rem',
      '@media (min-width:768px)': {
        fontSize: '1.5rem',
      },
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0,0,0,0.1)',
    '0px 4px 8px rgba(0,0,0,0.1)',
    '0px 8px 16px rgba(0,0,0,0.1)',
    '0px 12px 24px rgba(0,0,0,0.1)',
    ...Array(20).fill('0px 2px 4px rgba(0,0,0,0.1)'),
  ],
});

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#4DD0E1',
      light: '#80deea',
      dark: '#00BCD4',
      contrastText: '#000',
    },
    secondary: {
      main: '#42a5f5',
      light: '#90caf9',
      dark: '#1565C0',
      contrastText: '#000',
    },
    background: {
      default: '#0a1929',
      paper: '#1e293b',
    },
    text: {
      primary: '#e3f2fd',
      secondary: 'rgba(227, 242, 253, 0.7)',
    },
  },
  typography: {
    fontSize: 14,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.2,
      marginBottom: '1rem',
      '@media (min-width:768px)': {
        fontSize: '2.5rem',
      },
    },
    h2: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.3,
      marginBottom: '0.875rem',
      '@media (min-width:768px)': {
        fontSize: '2rem',
      },
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.4,
      marginBottom: '0.75rem',
      '@media (min-width:768px)': {
        fontSize: '1.75rem',
      },
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.4,
      marginBottom: '0.5rem',
      '@media (min-width:768px)': {
        fontSize: '1.5rem',
      },
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0,0,0,0.3)',
    '0px 4px 8px rgba(0,0,0,0.3)',
    '0px 8px 16px rgba(0,0,0,0.3)',
    '0px 12px 24px rgba(0,0,0,0.3)',
    ...Array(20).fill('0px 2px 4px rgba(0,0,0,0.3)'),
  ],
});


function LogTheme({ children }) {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  const theme = darkMode ? darkTheme : lightTheme;

  return (
    <DarkModeContext.Provider value={{ darkMode, setDarkMode }}>
      <ThemeProvider theme={theme}>
        <GlobalStyles styles={{
          body: {
            margin: 0,
            padding: 0,
            transition: 'background-color 0.3s ease, color 0.3s ease',
            backgroundColor: theme.palette.background.default,
          },
          '.welcome-background': {
            position: 'relative',
            minHeight: '400px',
            backgroundColor: '#F5F9FF',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(to bottom, rgba(245, 249, 255, 0.95) 0%, rgba(245, 249, 255, 0.98) 100%)',
              zIndex: 0,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: 'url(/images/bakgrund_loggboken_1000x1500.jpg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              opacity: 0.15,
              zIndex: 0,
            },
          },
          '.welcome-content': {
            position: 'relative',
            zIndex: 1,
          },
          '.ingress': {
            fontSize: '1.5rem',
            fontStyle: 'italic',
            marginBottom: '50px',
            width: '75%',
            color: theme.palette.text.primary,
          },
          '.image-300': {
            width: '300px',
            padding: '20px',
            borderRadius: theme.shape.borderRadius,
          },
          '.menylink': {
            color: 'white',
            fontWeight: '500 !important',
            fontSize: '18px !important',
            lineHeight: '25px !important',
            marginRight: '10px',
            textDecoration: 'none',
            transition: 'color 0.3s ease, opacity 0.3s ease',
          },
          '.menylink:hover': {
            cursor: 'pointer',
            color: '#ccc',
            opacity: 0.8,
            transition: 'color 0.3s ease, opacity 0.3s ease',
          },
          '.links': {
            display: 'none',
            flexDirection: 'column',
            position: 'absolute',
            top: '100%',
            left: '0',
            width: '100%',
            backgroundColor: '#333589',
            padding: '30px',
            boxShadow: theme.shadows[4],
            zIndex: 99,
            transition: 'all 0.3s ease',
          },
          '.links.show': {
            display: 'flex',
          },
          '.hamburger': {
            display: 'block',
            cursor: 'pointer',
            transition: 'transform 0.2s ease',
          },
          '.hamburger:hover': {
            transform: 'scale(1.1)',
          },
          '@media (min-width: 768px)': {
            '.links': {
              display: 'flex',
              flexDirection: 'row',
              position: 'static',
              top: 'auto',
              left: 'auto',
              width: 'auto',
              backgroundColor: 'transparent',
              padding: '0',
              boxShadow: 'none',
            },
            '.hamburger': {
              display: 'none !important',
            },
          },
          '@media print': {
            '.no-print': {
              display: 'none !important',
            },
            body: {
              backgroundColor: 'white',
              color: 'black',
            },
          },
        }} />
        {children}
      </ThemeProvider>
    </DarkModeContext.Provider>
  );
}

export default LogTheme;